import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield } from "lucide-react";

const STORAGE_KEY = "iqlog_cookie_consent_v1";

/**
 * GDPR / CCPA / APPI cookie consent banner.
 * Persists user choice in localStorage so the banner doesn't reappear.
 *
 * Categories:
 *   - essential   (always on, cannot be rejected)
 *   - analytics   (e.g. internal pageview metrics)
 *   - marketing   (advertising / remarketing)
 */
export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setOpen(true);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed?.version || parsed.version !== 1) {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const persist = (choice) => {
    const payload = {
      version: 1,
      essential: true,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      decided_at: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[60] border-t border-[#3B82F6]/40 bg-[#0A0E1A]/95 backdrop-blur-xl shadow-[0_-4px_30px_rgba(59,130,246,0.15)]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-md bg-[#3B82F6]/15 text-[#3B82F6] flex items-center justify-center shrink-0">
              <Cookie className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5">
                // COOKIE NOTICE · GDPR / CCPA / APPI
              </div>
              <h3 className="font-display text-lg lg:text-xl tracking-tight text-white" style={{ fontWeight: 800 }}>
                We respect your privacy
              </h3>
              <p className="text-sm text-[#94A3B8] mt-1.5 leading-relaxed">
                IQ Log uses essential cookies to keep the service running. With your
                consent, we may also use analytics &amp; marketing cookies to improve the
                product. You can change your choice anytime.{" "}
                <Link
                  to="/privacy"
                  data-testid="cookie-banner-privacy-link"
                  className="text-[#3B82F6] hover:underline"
                >
                  Privacy Policy
                </Link>
                {" · "}
                <Link
                  to="/data-request"
                  data-testid="cookie-banner-datarequest-link"
                  className="text-[#3B82F6] hover:underline"
                >
                  Data request
                </Link>
              </p>

              {showDetails && (
                <div className="mt-4 grid sm:grid-cols-3 gap-2 text-xs">
                  <label className="flex items-start gap-2 p-3 border border-[#1E2A3F] bg-[#0F1525] cursor-not-allowed opacity-80">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      data-testid="cookie-essential-checkbox"
                      className="mt-0.5 accent-[#3B82F6]"
                    />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#3B82F6]">
                        Essential · Required
                      </div>
                      <div className="text-[#94A3B8] mt-1">
                        Auth, session, anti-CSRF, payment integrity.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 border border-[#1E2A3F] bg-[#0F1525] cursor-pointer hover:border-[#3B82F6]/50">
                    <input
                      type="checkbox"
                      checked={prefs.analytics}
                      onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                      data-testid="cookie-analytics-checkbox"
                      className="mt-0.5 accent-[#3B82F6]"
                    />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                        Analytics
                      </div>
                      <div className="text-[#94A3B8] mt-1">
                        Anonymous pageview metrics to improve the product.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 border border-[#1E2A3F] bg-[#0F1525] cursor-pointer hover:border-[#3B82F6]/50">
                    <input
                      type="checkbox"
                      checked={prefs.marketing}
                      onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                      data-testid="cookie-marketing-checkbox"
                      className="mt-0.5 accent-[#3B82F6]"
                    />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                        Marketing
                      </div>
                      <div className="text-[#94A3B8] mt-1">
                        Personalized ads &amp; remarketing (none active by default).
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:flex-col lg:items-stretch lg:w-[220px] shrink-0">
            <button
              type="button"
              onClick={acceptAll}
              data-testid="cookie-accept-all-btn"
              className="px-4 py-2.5 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] neon-glow-soft"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={rejectAll}
              data-testid="cookie-reject-all-btn"
              className="px-4 py-2.5 border border-[#1E2A3F] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              Reject non-essential
            </button>
            {!showDetails ? (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                data-testid="cookie-customize-btn"
                className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] inline-flex items-center justify-center gap-1.5"
              >
                <Shield className="h-3 w-3" strokeWidth={2} /> Customize
              </button>
            ) : (
              <button
                type="button"
                onClick={savePrefs}
                data-testid="cookie-save-prefs-btn"
                className="px-4 py-2.5 border border-[#00FF94] text-[#00FF94] font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#00FF94]/10"
              >
                Save my choices
              </button>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        aria-label="Close"
        onClick={rejectAll}
        data-testid="cookie-close-btn"
        className="absolute top-3 right-3 p-1.5 text-[#64748B] hover:text-white"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
